/**
 * Business Wizard - Kreator profilu firmy
 * 4 steps: Podstawy → Usługi → Kontakt → Publikacja
 * Target: mikrofirmy, małe biznesy
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Building2, Briefcase, Phone, Globe, Loader2, Image as ImageIcon, Sparkles, Palette, GripVertical, Sun, Moon, ArrowDown, ArrowUp as ArrowUpIcon, ExternalLink, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ImageUploader } from '@/components/ImageUploader';
import PanelLayout from '@/components/PanelLayout';

import { API_BASE as API } from '@/utils/api';

const INDUSTRIES = [
  'Usługi fryzjerskie i kosmetyczne',
  'Mechanika i auto serwis',
  'Fotografia i wideo',
  'Trener personalny / Coach',
  'Usługi remontowe i budowlane',
  'Marketing i reklama',
  'IT i programowanie',
  'Gastronomia',
  'Edukacja i korepetycje',
  'Zdrowie i wellness',
  'Consulting i doradztwo',
  'Transport i logistyka',
  'Inna'
];

const PRICE_TYPES = [
  { value: 'fixed', label: 'Stała cena' },
  { value: 'from', label: 'Od' },
  { value: 'hourly', label: 'Za godzinę' },
  { value: 'free', label: 'Bezpłatne' },
  { value: 'contact', label: 'Wycena indywidualna' },
];

const STEPS = [
  { id: 0, title: 'Podstawy', icon: Building2 },
  { id: 1, title: 'Usługi', icon: Briefcase },
  { id: 2, title: 'Kontakt', icon: Phone },
  { id: 3, title: 'Zdjęcia', icon: ImageIcon },
  { id: 4, title: 'Personalizacja', icon: Palette },
  { id: 5, title: 'Publikacja', icon: Globe },
];

const ACCENT_COLORS = [
  { id: 'gold', label: 'Złoty', hex: '#c9a96e' },
  { id: 'blue', label: 'Niebieski', hex: '#6e8ec9' },
  { id: 'green', label: 'Zielony', hex: '#6ec98a' },
  { id: 'red', label: 'Czerwony', hex: '#c96e6e' },
  { id: 'purple', label: 'Fioletowy', hex: '#8e6ec9' },
  { id: 'teal', label: 'Turkusowy', hex: '#6ec9b8' },
  { id: 'pastel-pink', label: 'Pastel róż', hex: '#f4b8c1' },
  { id: 'pastel-blue', label: 'Pastel nieb.', hex: '#a8c8e8' },
  { id: 'pastel-mint', label: 'Pastel mięta', hex: '#a8dbc5' },
  { id: 'pastel-lavender', label: 'Lawenda', hex: '#c5b8d9' },
  { id: 'pastel-peach', label: 'Brzoskwinia', hex: '#f4c9a8' },
  { id: 'pastel-yellow', label: 'Pastel żółty', hex: '#f0e0a0' },
];

const DEFAULT_SECTIONS = ['uslugi', 'galeria', 'o-nas', 'kontakt'];
const SECTION_LABELS = { 'uslugi': 'Usługi', 'galeria': 'Galeria', 'o-nas': 'O nas', 'kontakt': 'Kontakt' };

export default function BusinessWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [businessId, setBusinessId] = useState(id || null);
  const [loading, setLoading] = useState(!!id);
  const [isUploading, setIsUploading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const [form, setForm] = useState({
    name: '', industry: '', description: '', short_description: '',
    services: [],
    phone: '', email: '', website: '', address: '', city: '', postal_code: '',
    opening_hours: '', social_facebook: '', social_instagram: '',
    cover_photo: '', photos: [], logo: '',
    seo_title: '', seo_description: '',
    accent_color: 'gold', theme: 'dark',
    section_order: ['uslugi', 'galeria', 'o-nas', 'kontakt'],
  });

  const token = localStorage.getItem('token');

  // Load existing business if editing
  useEffect(() => {
    if (id) {
      fetch(`${API}/api/business/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => {
          if (r.status === 401) {
            toast.error('Sesja wygasła — zaloguj się ponownie');
            localStorage.removeItem('token');
            window.location.href = '/login';
            return {};
          }
          return r.json();
        })
        .then(data => {
          if (data.id) {
            setForm(prev => ({ ...prev, ...data }));
            setBusinessId(data.id);
          }
        })
        .catch(() => toast.error(t('wizards.saveError')))
        .finally(() => setLoading(false));
    }
  }, [id, token]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validateStep = () => {
    if (step === 0) return form.name.trim().length >= 2 && form.industry;
    if (step === 2) return form.phone.trim().length >= 6 || form.email.trim().length >= 5;
    return true;
  };

  const saveData = async (publish = false) => {
    setSaving(true);
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        toast.error(t('toasts.sessionExpired'));
        window.location.href = '/login';
        return null;
      }
      const payload = { ...form };
      if (publish) payload.is_published = true;

      let res;
      if (businessId) {
        res = await fetch(`${API}/api/business/${businessId}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API}/api/business/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        if (res.status === 401) {
          toast.error(t('toasts.sessionExpired'));
          localStorage.removeItem('token');
          window.location.href = '/login';
          return null;
        }
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || t('wizards.saveError'));
        return null;
      }

      const data = await res.json();
      if (data.id) {
        setBusinessId(data.id);
        setForm(prev => ({ ...prev, ...data }));
        if (publish) {
          toast.success(t('wizards.publishSuccess'));
          navigate('/panel/uslugi');
        }
        return data;
      } else {
        toast.error(t('wizards.saveError'));
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error(t('wizards.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const nextStep = async () => {
    if (!validateStep()) {
      toast.error(t('wizards.validationError'));
      return;
    }
    await saveData();
    setStep(s => Math.min(s + 1, 5));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  // Section order management
  const moveSectionUp = (idx) => {
    if (idx === 0) return;
    const arr = [...form.section_order];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    update('section_order', arr);
  };
  const moveSectionDown = (idx) => {
    if (idx >= form.section_order.length - 1) return;
    const arr = [...form.section_order];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    update('section_order', arr);
  };

  // Service management
  const addService = () => {
    update('services', [...form.services, { id: Date.now().toString(36), name: '', description: '', price: '', price_type: 'fixed' }]);
  };

  const updateService = (idx, field, value) => {
    const updated = [...form.services];
    updated[idx] = { ...updated[idx], [field]: value };
    update('services', updated);
  };

  const removeService = (idx) => {
    update('services', form.services.filter((_, i) => i !== idx));
  };

  const generateAIDescription = async () => {
    if (!form.name || !form.industry) {
      toast.error(t('wizards.validationError'));
      return;
    }
    setAiGenerating(true);
    try {
      const res = await fetch(`${API}/api/business/generate-description`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, industry: form.industry, city: form.city, services: form.services }),
      });
      const data = await res.json();
      if (data.ok) {
        setForm(prev => ({
          ...prev,
          description: data.description || prev.description,
          short_description: data.short_description || prev.short_description,
          seo_title: data.seo_title || prev.seo_title,
          seo_description: data.seo_description || prev.seo_description,
        }));
        toast.success(t('shareCenter.generated'));
      } else {
        toast.error(t('shareCenter.generateError'));
      }
    } catch {
      toast.error(t('shareCenter.generateError'));
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-zinc-400" /></div>;

  return (
    <PanelLayout>
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-zinc-900">{businessId ? t('businessWizard.editTitle') : t('businessWizard.title')}</h1>
          <div className="flex gap-2 mt-3">
            {STEPS.map((s) => (
              <button
                key={s.id}
                onClick={() => s.id <= step && setStep(s.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  s.id === step ? 'bg-gray-900 text-white' : s.id < step ? 'bg-green-100 text-green-800' : 'bg-zinc-100 text-zinc-400'
                }`}
                data-testid={`wizard-step-${s.id}`}
              >
                {s.id < step ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Step 0: Basics */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-basics">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Nazwa firmy *</label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="np. Studio Fryzjerskie Anna" data-testid="business-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Branża *</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind}
                    onClick={() => update('industry', ind)}
                    className={`px-3 py-2 rounded-lg text-sm text-left border transition-all ${
                      form.industry === ind ? 'border-gray-900 bg-gray-900 text-white' : 'border-zinc-200 hover:border-gray-400'
                    }`}
                    data-testid={`industry-${ind.substring(0,10)}`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Krótki opis (1-2 zdania)</label>
              <Input value={form.short_description} onChange={e => update('short_description', e.target.value)} placeholder="Czym się zajmujesz? Dla kogo?" data-testid="short-description" />
              <p className="text-xs text-zinc-500 mt-1">Pojawi się pod nazwą firmy na stronie i w wynikach Google</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Pełny opis firmy</label>
              <Textarea value={form.description} onChange={e => update('description', e.target.value)} rows={5} placeholder="Opisz swoją firmę, doświadczenie, co Cię wyróżnia..." data-testid="full-description" />
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={generateAIDescription}
              disabled={aiGenerating || !form.name || !form.industry}
              className="gap-2 border-orange-200 text-orange-700 hover:bg-orange-50 w-full"
              data-testid="ai-generate-btn"
            >
              {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiGenerating ? 'AI generuje opisy...' : 'Wygeneruj opisy AI (opis, SEO)'}
            </Button>
          </div>
        )}

        {/* Step 1: Services */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-services">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">Twoje usługi / produkty</h2>
                <p className="text-sm text-zinc-500">Dodaj to, co oferujesz klientom</p>
              </div>
              <Button onClick={addService} size="sm" className="gap-1" data-testid="add-service-btn">
                <Plus className="w-4 h-4" /> Dodaj
              </Button>
            </div>

            {form.services.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Briefcase className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-zinc-500">Brak usług — kliknij "Dodaj" powyżej</p>
              </div>
            )}

            {form.services.map((svc, idx) => (
              <div key={svc.id || idx} className="bg-white p-4 rounded-lg border space-y-3" data-testid={`service-${idx}`}>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input value={svc.name} onChange={e => updateService(idx, 'name', e.target.value)} placeholder="Nazwa usługi / produktu" />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeService(idx)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea value={svc.description} onChange={e => updateService(idx, 'description', e.target.value)} placeholder="Krótki opis (opcjonalnie)" rows={2} />
                <div className="flex gap-3">
                  <select
                    value={svc.price_type}
                    onChange={e => updateService(idx, 'price_type', e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                  </select>
                  {svc.price_type !== 'free' && svc.price_type !== 'contact' && (
                    <Input value={svc.price} onChange={e => updateService(idx, 'price', e.target.value)} placeholder="np. 150 zł" className="w-40" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 2: Contact */}
        {step === 2 && (
          <div className="space-y-5" data-testid="step-contact">
            <h2 className="text-lg font-semibold text-zinc-900">Dane kontaktowe</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Telefon *</label>
                <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+48 600 123 456" data-testid="phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                <Input value={form.email} onChange={e => update('email', e.target.value)} placeholder="kontakt@firma.pl" data-testid="email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Miasto</label>
                <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder="Warszawa" data-testid="city" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Adres</label>
                <Input value={form.address} onChange={e => update('address', e.target.value)} placeholder="ul. Przykładowa 10" data-testid="address" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Strona www</label>
              <Input value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://www.firma.pl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Godziny otwarcia</label>
              <Input value={form.opening_hours} onChange={e => update('opening_hours', e.target.value)} placeholder="Pon-Pt 9:00-17:00, Sob 10:00-14:00" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Facebook</label>
                <Input value={form.social_facebook} onChange={e => update('social_facebook', e.target.value)} placeholder="https://facebook.com/firma" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Instagram</label>
                <Input value={form.social_instagram} onChange={e => update('social_instagram', e.target.value)} placeholder="https://instagram.com/firma" />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div className="space-y-5" data-testid="step-photos">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Zdjęcia firmy</h2>
              <p className="text-sm text-zinc-500">Profile ze zdjęciami mają 5x więcej zapytań</p>
            </div>
            <ImageUploader
              coverImage={form.cover_photo}
              gallery={form.photos}
              onCoverChange={(url) => update('cover_photo', url)}
              onGalleryChange={(urls) => update('photos', urls)}
              onUploadingChange={setIsUploading}
              maxGalleryImages={10}
            />
            {isUploading && (
              <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
                <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                <span className="text-sm text-orange-700">Przesyłanie zdjęć...</span>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Personalization */}
        {step === 4 && (
          <div data-testid="step-personalization">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-900">Personalizacja strony</h2>
              <p className="text-sm text-zinc-500">Dostosuj wyglad strony do swojej marki</p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* LEFT: Config options */}
              <div className="space-y-6">
                {/* Theme */}
                <div className="bg-white border rounded-xl p-5">
                  <label className="block text-sm font-semibold text-zinc-900 mb-3">Motyw strony</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => update('theme', 'dark')}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${form.theme === 'dark' ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      data-testid="theme-dark"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center">
                        <Moon className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-zinc-900 text-sm">Wersja ciemna</p>
                        <p className="text-xs text-zinc-500">Styl premium</p>
                      </div>
                    </button>
                    <button
                      onClick={() => update('theme', 'light')}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${form.theme === 'light' ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      data-testid="theme-light"
                    >
                      <div className="w-10 h-10 rounded-full bg-zinc-100 border flex items-center justify-center">
                        <Sun className="w-5 h-5 text-zinc-700" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-zinc-900 text-sm">Wersja jasna</p>
                        <p className="text-xs text-zinc-500">Styl klasyczny</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="bg-white border rounded-xl p-5">
                  <label className="block text-sm font-semibold text-zinc-900 mb-3">Kolor akcentu</label>
                  <p className="text-xs text-zinc-500 mb-2">Klasyczne</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {ACCENT_COLORS.filter(c => !c.id.startsWith('pastel')).map(color => (
                      <button
                        key={color.id}
                        onClick={() => update('accent_color', color.id)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${form.accent_color === color.id ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`accent-${color.id}`}
                      >
                        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: color.hex }} />
                        <span className="text-xs text-zinc-600">{color.label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mb-2 mt-4">Pastelowe</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {ACCENT_COLORS.filter(c => c.id.startsWith('pastel')).map(color => (
                      <button
                        key={color.id}
                        onClick={() => update('accent_color', color.id)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${form.accent_color === color.id ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`accent-${color.id}`}
                      >
                        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: color.hex }} />
                        <span className="text-xs text-zinc-600">{color.label}</span>
                      </button>
                    ))}
                  </div>
                  {/* Mini preview */}
                  <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: form.theme === 'dark' ? '#0a0a0a' : '#fafaf8' }}>
                    <p style={{ color: ACCENT_COLORS.find(c => c.id === form.accent_color)?.hex, fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 500 }}>
                      PODGLAD AKCENTU
                    </p>
                    <p style={{ color: form.theme === 'dark' ? '#f5f5f0' : '#1a1a1a', fontFamily: "'Playfair Display', serif", fontSize: '24px', marginTop: '8px' }}>
                      {form.name || 'Twoja Firma'}
                    </p>
                    <button
                      className="mt-3 px-6 py-2 text-xs font-semibold tracking-wider uppercase"
                      style={{ backgroundColor: ACCENT_COLORS.find(c => c.id === form.accent_color)?.hex, color: form.theme === 'dark' ? '#0a0a0a' : '#fff' }}
                      data-testid="accent-preview-btn"
                    >
                      Przykladowy przycisk
                    </button>
                  </div>
                </div>

                {/* Logo */}
                <div className="bg-white border rounded-xl p-5">
                  <label className="block text-sm font-semibold text-zinc-900 mb-1">Logo firmy</label>
                  <p className="text-xs text-zinc-500 mb-3">Wyswietli sie w nawigacji strony (opcjonalne)</p>
                  <div className="flex items-center gap-4">
                    {form.logo ? (
                      <div className="relative">
                        <img src={form.logo.startsWith('http') ? form.logo : `${API}/api${form.logo}`} alt="Logo" className="w-16 h-16 object-contain rounded-lg border" />
                        <button onClick={() => update('logo', '')} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : null}
                    <ImageUploader
                      coverImage={form.logo}
                      gallery={[]}
                      onCoverChange={(url) => update('logo', url)}
                      onGalleryChange={() => {}}
                      onUploadingChange={setIsUploading}
                      maxGalleryImages={0}
                    />
                  </div>
                </div>

                {/* Section Order */}
                <div className="bg-white border rounded-xl p-5">
                  <label className="block text-sm font-semibold text-zinc-900 mb-1">Kolejnosc sekcji</label>
                  <p className="text-xs text-zinc-500 mb-3">Zmień kolejność sekcji na stronie</p>
                  <div className="space-y-2">
                    {(form.section_order || DEFAULT_SECTIONS).map((sec, idx) => (
                      <div key={sec} className="flex items-center gap-3 p-3 bg-zinc-50 border rounded-lg" data-testid={`section-order-${sec}`}>
                        <GripVertical className="w-4 h-4 text-gray-300" />
                        <span className="flex-1 text-sm font-medium text-zinc-700">{SECTION_LABELS[sec] || sec}</span>
                        <span className="text-xs text-zinc-400 mr-2">{idx + 1}</span>
                        <button onClick={() => moveSectionUp(idx)} disabled={idx === 0} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-20" data-testid={`section-up-${sec}`}>
                          <ArrowUpIcon className="w-4 h-4 text-zinc-500" />
                        </button>
                        <button onClick={() => moveSectionDown(idx)} disabled={idx >= (form.section_order || DEFAULT_SECTIONS).length - 1} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-20" data-testid={`section-down-${sec}`}>
                          <ArrowDown className="w-4 h-4 text-zinc-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT: Live Preview */}
              <div className="space-y-3">
                <div className="bg-white border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="block text-sm font-semibold text-zinc-900">Podglad na zywo</label>
                      <p className="text-xs text-zinc-500">Kliknij Dalej i wróć aby odświeżyć</p>
                    </div>
                    {form.slug && (
                      <a href={`/firma/${form.slug}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        data-testid="preview-open-tab">
                        <ExternalLink className="w-3.5 h-3.5" /> Nowa karta
                      </a>
                    )}
                  </div>
                  {form.slug ? (
                    <div className="border rounded-lg overflow-hidden bg-zinc-100" style={{ height: '500px' }}>
                      <iframe
                        src={`${window.location.origin}/firma/${form.slug}?preview=1&t=${Date.now()}`}
                        title="Podglad strony"
                        className="w-full h-full border-0"
                        style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}
                        data-testid="live-preview-iframe"
                      />
                    </div>
                  ) : (
                    <div className="border rounded-lg bg-zinc-50 flex items-center justify-center" style={{ height: '300px' }}>
                      <div className="text-center text-zinc-400">
                        <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Podgląd będzie dostępny po zapisaniu strony</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Publish + SEO */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-publish">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">SEO i publikacja</h2>
              <p className="text-sm text-zinc-500">Optymalizuj stronę pod wyszukiwarki Google</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Tytuł SEO</label>
              <Input
                value={form.seo_title || `${form.name} — ${form.industry} ${form.city}`}
                onChange={e => update('seo_title', e.target.value)}
                placeholder="Tytuł widoczny w Google"
                data-testid="seo-title"
              />
              <p className="text-xs text-zinc-500 mt-1">{(form.seo_title || `${form.name} — ${form.industry} ${form.city}`).length}/60 znaków</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Opis SEO</label>
              <Textarea
                value={form.seo_description || form.short_description}
                onChange={e => update('seo_description', e.target.value)}
                placeholder="Opis widoczny w wynikach Google (150-160 znaków)"
                rows={3}
                data-testid="seo-description"
              />
              <p className="text-xs text-zinc-500 mt-1">{(form.seo_description || form.short_description || '').length}/160 znaków</p>
            </div>

            {/* Preview */}
            <div className="bg-white border rounded-lg p-5">
              <h3 className="text-sm font-semibold text-zinc-500 mb-3">Podgląd w Google:</h3>
              <div className="space-y-1">
                <p className="text-blue-700 text-lg font-medium">{form.seo_title || `${form.name} — ${form.industry} ${form.city}`}</p>
                <p className="text-green-700 text-sm">apisai.pl/firma/{form.name ? form.name.toLowerCase().replace(/\s+/g, '-').substring(0, 20) : '...'}</p>
                <p className="text-zinc-600 text-sm">{form.seo_description || form.short_description || 'Opis Twojej firmy...'}</p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-zinc-50 border rounded-lg p-5 space-y-2">
              <h3 className="font-semibold text-zinc-900">Podsumowanie</h3>
              <p className="text-sm"><span className="text-zinc-500">Firma:</span> {form.name}</p>
              <p className="text-sm"><span className="text-zinc-500">Branża:</span> {form.industry}</p>
              <p className="text-sm"><span className="text-zinc-500">Usługi:</span> {form.services.length}</p>
              <p className="text-sm"><span className="text-zinc-500">Kontakt:</span> {form.phone} {form.email ? `/ ${form.email}` : ''}</p>
              <p className="text-sm"><span className="text-zinc-500">Miasto:</span> {form.city || '—'}</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <Button variant="outline" onClick={step === 0 ? () => navigate(-1) : prevStep} data-testid="wizard-prev">
            <ArrowLeft className="w-4 h-4 mr-2" /> {step === 0 ? t('wizards.cancel') : t('wizards.back')}
          </Button>
          
          {step < 5 ? (
            <Button onClick={nextStep} disabled={saving || isUploading} data-testid="wizard-next">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('wizards.next')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => saveData(false)} disabled={saving} data-testid="save-draft">
                {t('businessWizard.saveDraft')}
              </Button>
              <Button onClick={() => saveData(true)} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="publish-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                {t('businessWizard.publishNow')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    </PanelLayout>
  );
}
